import { useState, useCallback, useRef } from 'react';
import { Rocket, Monitor } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { usePrestacoes } from '@/lib/hooks/prestacoes';
import { MobileTopbar } from '@/mobile/shared/MobileTopbar';
import { PrestHistoryItem } from './PrestHistoryItem';
import { PrestDetailModule } from './PrestDetailModule';
import { PrestacaoActiveModule } from './PrestacaoActiveModule';
import type { PrestHistoryItemData, PrestacaoStatus } from './types';

/** Formata competência "YYYY-MM" → "Mês AAAA" */
function fmtCompetencia(c: string): string {
  const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];
  const [year, month] = c.split('-');
  const idx = parseInt(month ?? '1', 10) - 1;
  return `${MONTHS[idx] ?? month} ${year}`;
}

function statusLabel(s: PrestacaoStatus): string {
  const MAP: Record<PrestacaoStatus, string> = {
    RASCUNHO: 'rascunho',
    PRONTA: 'pronta',
    AUTOMATING: 'em automação',
    AGUARDANDO_HUMANO: 'aguardando conclusão',
    CONCLUIDA: 'concluída',
    ERRO: 'erro',
    CANCELADA: 'cancelada',
  };
  return MAP[s] ?? s.toLowerCase();
}

function statusDot(s: PrestacaoStatus): string {
  if (s === 'CONCLUIDA') return 'var(--pass)';
  if (s === 'PRONTA') return 'var(--mint-deep)';
  if (s === 'AUTOMATING') return 'var(--info, var(--mint-deep))';
  if (s === 'AGUARDANDO_HUMANO') return 'var(--warn)';
  if (s === 'ERRO') return 'var(--block)';
  return 'var(--fg-dim)';
}

/** Card flutuante hero da prestação em aberto (próxima / ativa) */
function NextPrestCard({
  id,
  competencia,
  status,
  onGoActive,
}: {
  id: string;
  competencia: string;
  status: PrestacaoStatus;
  onGoActive: (id: string) => void;
}) {
  const isActive = status === 'AUTOMATING' || status === 'AGUARDANDO_HUMANO';
  const isReady = status === 'PRONTA';

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        padding: 16,
        boxShadow: 'var(--shadow-clay)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            borderRadius: 'var(--r-pill)',
            fontSize: 10.5,
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            background: isActive
              ? 'var(--info-soft, var(--mint-soft))'
              : isReady
              ? 'var(--mint-soft)'
              : 'var(--card-soft)',
            color: isActive ? 'var(--info, var(--mint-deep))' : isReady ? 'var(--mint-deep)' : 'var(--fg-muted)',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: statusDot(status),
            }}
            aria-hidden
          />
          {statusLabel(status)}
        </span>
        <span
          style={{
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: 'var(--fg-muted)',
          }}
        >
          Próxima prestação
        </span>
      </div>

      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: 'var(--fg)',
        }}
      >
        {fmtCompetencia(competencia)}
      </div>

      {isActive ? (
        <button
          type="button"
          onClick={() => onGoActive(id)}
          aria-label="Acompanhar automação em andamento"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginTop: 14,
            padding: '12px 18px',
            borderRadius: 'var(--r-3)',
            background: 'var(--mint-deep)',
            color: 'var(--on-ink)',
            fontSize: 14,
            fontWeight: 700,
            border: 'none',
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          <Rocket size={16} strokeWidth={1.8} aria-hidden />
          Acompanhar automação
        </button>
      ) : (
        /* Botão desabilitado com tooltip D2 */
        <div
          title="Para iniciar a automação, acesse o GestAKI no computador"
          style={{ position: 'relative' }}
        >
          <button
            type="button"
            disabled
            aria-label="Iniciar automação — disponível apenas no computador"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 14,
              padding: '12px 18px',
              borderRadius: 'var(--r-3)',
              background: 'var(--card-soft)',
              color: 'var(--fg-dim)',
              fontSize: 14,
              fontWeight: 700,
              border: '1px solid var(--card-stroke)',
              cursor: 'not-allowed',
              minHeight: 44,
              opacity: 0.7,
            }}
          >
            <Monitor size={16} strokeWidth={1.8} aria-hidden />
            Iniciar automação — disponível no computador
          </button>
        </div>
      )}
    </div>
  );
}

type SubView = 'list' | 'active' | 'detail';

interface PrestModuleState {
  view: SubView;
  activePrestId: string | null;
  selectedPrest: PrestHistoryItemData | null;
}

/**
 * Orquestrador do módulo Prestação.
 * Sub-views: lista → detalhe histórico | lista → active (read-only).
 */
export function PrestacaoModule() {
  const { user } = useAuth();
  const wsId = user?.effectiveWorkspaceId ?? null;
  const { data, isLoading } = usePrestacoes(wsId);
  const scrollYRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);

  const [nav, setNav] = useState<PrestModuleState>({
    view: 'list',
    activePrestId: null,
    selectedPrest: null,
  });

  const goToActive = useCallback((id: string) => {
    scrollYRef.current = listRef.current?.scrollTop ?? 0;
    setNav({ view: 'active', activePrestId: id, selectedPrest: null });
  }, []);

  const goToDetail = useCallback((p: PrestHistoryItemData) => {
    scrollYRef.current = listRef.current?.scrollTop ?? 0;
    setNav({ view: 'detail', activePrestId: null, selectedPrest: p });
  }, []);

  const goBack = useCallback(() => {
    setNav({ view: 'list', activePrestId: null, selectedPrest: null });
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = scrollYRef.current;
    });
  }, []);

  /* Sub-views */
  if (nav.view === 'active' && nav.activePrestId) {
    return (
      <PrestacaoActiveModule
        prestacaoId={nav.activePrestId}
        workspaceId={wsId}
        onBack={goBack}
      />
    );
  }
  if (nav.view === 'detail' && nav.selectedPrest) {
    return <PrestDetailModule prest={nav.selectedPrest} workspaceId={wsId} onBack={goBack} />;
  }

  const items = data?.items ?? [];

  /* Separa "próxima" (não concluída/cancelada) do histórico */
  const nextPrest = items.find(
    (p) => p.status !== 'CONCLUIDA' && p.status !== 'CANCELADA',
  );
  const history = items
    .filter((p) => p.status === 'CONCLUIDA')
    .map<PrestHistoryItemData>((p) => ({
      id: p.id,
      label: fmtCompetencia(p.competencia),
      competencia: p.competencia,
      status: p.status,
      sentAt: p.concluidaEm,
      concluidaEm: p.concluidaEm,
    }));

  return (
    <div
      ref={listRef}
      style={{
        height: '100%',
        overflowY: 'auto',
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: '8px 18px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <MobileTopbar title="Prestação" subtitle="Suas prestações de contas" />

        {/* Skeleton loading */}
        {isLoading && (
          <div
            style={{
              background: 'var(--card-soft)',
              borderRadius: 'var(--r-3)',
              height: 130,
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
            aria-busy="true"
            aria-label="Carregando prestações"
          />
        )}

        {/* Card da próxima prestação */}
        {!isLoading && nextPrest && (
          <NextPrestCard
            id={nextPrest.id}
            competencia={nextPrest.competencia}
            status={nextPrest.status}
            onGoActive={goToActive}
          />
        )}

        {/* Empty state */}
        {!isLoading && items.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 0',
              color: 'var(--fg-muted)',
              fontSize: 13,
            }}
          >
            Nenhuma prestação ainda — crie uma no computador para começar.
          </div>
        )}

        {/* Histórico */}
        {!isLoading && history.length > 0 && (
          <div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: 'var(--fg-muted)',
                marginBottom: 10,
              }}
            >
              Histórico
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map((p) => (
                <PrestHistoryItem key={p.id} prest={p} onOpen={goToDetail} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
